import { useEffect, useRef, useState } from "react";
import styles from "./GeminiChat.module.css";
import { ReactComponent as DoubtArrowIcon } from "../../assets/doubtsArrowIcon.svg";
import { ReactComponent as GeminiIcon } from "../../assets/GeminiIcon.svg";
import {
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Markdown from "react-markdown";
import axios from "axios";

const promptExtra = ` Return only raw Python script. No extra text, no comments unless necessary for the code.`;
const GeminiChat = ({ prompt }) => {
  const userId = localStorage.getItem("userId");
  const [solution, setSolution] = useState("");
  const [doubt, setDoubt] = useState(prompt ?? "");
  const [doubtLoading, setDoubtLoading] = useState(false);
  const [isGeminiOpen, setIsGeminiOpen] = useState(false);
  const chatEndRef = useRef(null);
  // console.log(process.env.REACT_APP_GITHUB_TOKEN, " token");
  const generateColabLink = async (code) => {
    const cleanCode = code.replace("```python", "") 
    .replace("```", "")      
    .trim();
    const notebook = {
      cells: [
        {
          cell_type: "code",
          metadata: {},
          source: cleanCode,
          outputs: [],
          execution_count: null,
        },
      ],
      metadata: { language_info: { name: "python3" } },
      nbformat: 4,
      nbformat_minor: 2,
    };
    try {
      const gistResponse = await fetch("https://api.github.com/gists", {
        method: "POST",
        headers: {
          Authorization: `token ${process.env.REACT_APP_GITHUB_TOKEN}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          description: "Generated from Gemini chat",
          public: true,
          files: {
            "gemini_code.ipynb": {
              content: JSON.stringify(notebook, null, 2),
            },
          },
        }),
      });

      const gistData = await gistResponse.json();
      const gistId = gistData.id;
      const colabUrl = `https://colab.research.google.com/gist/rrsmartsa/${gistId}/gemini_code.ipynb`;

      console.log(colabUrl, "got the colab");

      return colabUrl;
    } catch (error) {
      console.error(
        "Failed to generate Colab link:",
        error.response?.data || error.message
      );
      return null;
    }
  };

  // Function to fetch a solution to a user's doubt
  const fetchDoubt = async () => {
    if (doubt.trim().length == 0) {
      return;
    }
    const userMessage = { sender: "You", text: doubt };
    setSolution((prev) => [...prev, userMessage, { sender: "Gemini", text: "" }]);
    setDoubtLoading(true);
    setDoubt("");
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_KEY}/doubts/text/${userId}`,
      {
        prompt: doubt + (solution.length > 1 ? promptExtra : ''),
      }
    );
    // Set the solution received from the API to the state
    setSolution((prev) => {
      const updated = [...prev];
      updated[updated.length - 1] = { sender: "Gemini", text: response?.data?.data };
      return updated;
    });
    setDoubtLoading(false);
  };
  const handleDoubtSubmit = (e) => {
    if (e.key === "Enter") {
      fetchDoubt();
    }
  };
  const copyPrompt = async (output) => {
    const cleanCode = output.replace("```python", "") 
    .replace("```", "")        
    .trim();
    try {
      await navigator.clipboard.writeText(cleanCode);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };
  useEffect(() => {
    if (doubtLoading) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [doubtLoading]);
  useEffect(() => {
    setDoubt(prompt ?? "");
  }, [prompt]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        margin: "0 5vw",
        paddingBottom: "10rem"
      }}
    >
      <div className={styles.solutionContainer}>
        {solution.length > 0 && (
          <div className={styles.chatLog}>
            {solution.map((msg, index) => (
              <div
                key={index}
                className={
                  msg.sender === "Gemini"
                    ? styles.geminiMessage
                    : styles.userMessage
                }
              >
                <strong>
                  {msg.sender}:{" "}
                  {msg.sender === "Gemini" && (
                    <>
                      <button
                        className={styles.colabButton}
                        onClick={async () => {
                          const colabLink = await generateColabLink(msg.text);
                          window.open(colabLink, "_blank");
                        }}
                      >
                        Open in Colab
                      </button>
                      <ContentCopyIcon onClick={() => copyPrompt(msg.text)} sx={{marginLeft: '1vw', verticalAlign: 'middle', cursor: 'pointer'}} />
                    </>
                  )}
                </strong>
                {msg.sender == "You" ? (
                  msg.text.length < 100 ? msg.text : msg.text.substring(0, 200) + "..."
                ) : (
                  doubtLoading && msg.text === "" ? <CircularProgress sx={{display: 'block'}} /> : <Markdown>{msg.text}</Markdown>
                )}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
        )}
      </div>
      <div className={styles.doubtContainer}>
        {isGeminiOpen ? (
          <TextField
            type="text"
            placeholder="Need help with code? Ask here…"
            autoComplete="off"
            className={styles.doubtInput}
            onKeyDown={handleDoubtSubmit}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" style={{ cursor: "pointer" }}>
                  <IconButton onClick={fetchDoubt}>
                    <DoubtArrowIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            value={doubt}
            onChange={(e) => {
              setDoubt(e.target.value);
            }}
            onSubmit={fetchDoubt}
          />
        ) : (
          <span
            style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}
          >
            <GeminiIcon
              onClick={() => setIsGeminiOpen(!isGeminiOpen)}
              style={{ cursor: "pointer" }}
            />
            Click to Generate Code
          </span>
        )}
      </div>
    </div>
  );
};

export default GeminiChat;
